import { ApolloClient, NormalizedCacheObject, useMutation } from '@apollo/client';
import { useTranslation } from 'next-i18next';
import { GetServerSidePropsContext } from 'next/types';
import { FORGOT_PASSWORD, LOGIN, RECOVER_PASSWORD, SIGN_UP, VERIFY_ACCOUNT } from '@/graphql/mutations';
import { VERIFY_FORGOT_PASSWORD_TOKEN, ME } from '@/graphql/queries';
import {
    ForgotUserPasswordMutation,
    ForgotUserPasswordMutationVariables,
    LoginMutation,
    MeQuery,
    RecoverPasswordMutation,
    RecoverPasswordMutationVariables,
    SignUpMutation,
    SignUpMutationVariables,
    VerifyAccountMutation,
} from '@/graphql/types';

export const useSignup = () => {
    const { i18n } = useTranslation(); // Translations
    const [signupMutation, { loading, error }] = useMutation<SignUpMutation, SignUpMutationVariables>(SIGN_UP, {
        context: { headers: { 'X-Language': i18n.language } },
    });

    const signup = async ({
        variables,
        onCompleted,
        onUpdate,
        onError,
    }: {
        variables?: SignUpMutationVariables;
        onCompleted?: (data?: SignUpMutation) => Promise<void>;
        onUpdate?: (status?: number | null, errors?: ({ field: string; messages: string[] } | null)[] | null) => void;
        onError?: (error: unknown) => Promise<void>;
    }) => {
        try {
            const { data } = await signupMutation({
                variables,
                update: (cache, result) => {
                    const { status, errors } = result?.data?.signup || {};
                    if (onUpdate) onUpdate(status, errors);
                },
                onCompleted: (dat) => {
                    if (onCompleted) void onCompleted(dat);
                },
                onError: (err) => {
                    if (onError) void onError(err);
                },
            });

            return data?.signup;
        } catch (err) {
            if (onError) void onError(err);
            throw err;
        }
    };

    return { signup, loading, error };
};

export const setAccountVerification = async (email: string, token: string, apolloClient: ApolloClient<NormalizedCacheObject>, context: GetServerSidePropsContext) => {
    const { data, errors } = await apolloClient.mutate<VerifyAccountMutation>({
        mutation: VERIFY_ACCOUNT,
        variables: {
            email,
            token,
        },
        context: { headers: { 'X-Language': context?.locale } },
        fetchPolicy: 'no-cache',
    });

    return { data: data?.verifyAccount, error: errors };
};

export const useForgotPassword = () => {
    const { i18n } = useTranslation(); // Translations
    const [forgotPasswordMutation, { loading, error }] = useMutation<ForgotUserPasswordMutation, ForgotUserPasswordMutationVariables>(FORGOT_PASSWORD, {
        context: { headers: { 'X-Language': i18n.language } },
    });

    const forgotPassword = async ({
        variables,
        onCompleted,
        onUpdate,
        onError,
    }: {
        variables?: ForgotUserPasswordMutationVariables;
        onCompleted?: (data?: ForgotUserPasswordMutation) => Promise<void>;
        onUpdate?: (status?: number | null, errors?: ({ field: string; messages: string[] } | null)[] | null) => void;
        onError?: (error: unknown) => Promise<void>;
    }) => {
        try {
            const { data } = await forgotPasswordMutation({
                variables,
                update: (cache, result) => {
                    const { status, errors } = result?.data?.forgotPassword || {};
                    if (onUpdate) onUpdate(status, errors);
                },
                onCompleted: (dat) => {
                    if (onCompleted) void onCompleted(dat);
                },
                onError: (err) => {
                    if (onError) void onError(err);
                },
            });

            return data?.forgotPassword;
        } catch (err) {
            if (onError) void onError(err);
            throw err;
        }
    };

    return { forgotPassword, loading, error };
};

export const getLogin = async (
    credentials: Record<'email' | 'language' | 'password' | 'recaptchaToken', string> | undefined,
    apolloClient: ApolloClient<NormalizedCacheObject>
) => {
    const { data, errors } = await apolloClient.mutate<LoginMutation>({
        mutation: LOGIN,
        context: { headers: { 'X-Language': credentials?.language } },
        variables: {
            email: credentials?.email,
            password: credentials?.password,
            recaptchaToken: credentials?.recaptchaToken,
        },
        fetchPolicy: 'no-cache',
    });

    return {
        response: data?.login?.response,
        errors: data?.login?.errors,
        status: data?.login?.status,
        graphQlErrors: errors,
    };
};

export type VerifyForgotPasswordQueryData = {
    verifyForgotPassword: {
        isValid: boolean;
    } | null;
};

export const getVerifyForgotPasswordToken = async (email: string, token: string, apolloClient: ApolloClient<NormalizedCacheObject>, context: GetServerSidePropsContext) => {
    const { data } = await apolloClient.query<VerifyForgotPasswordQueryData>({
        query: VERIFY_FORGOT_PASSWORD_TOKEN,
        context: { headers: { 'X-Language': context?.locale } },
        variables: {
            email: email,
            token: token,
        },
        fetchPolicy: 'no-cache',
    });

    return {
        isValid: data?.verifyForgotPassword?.isValid || false,
    };
};

export const useRecoverPassword = () => {
    const { i18n } = useTranslation(); // Translations
    const [recoverPasswordMutation, { loading, error }] = useMutation<RecoverPasswordMutation, RecoverPasswordMutationVariables>(RECOVER_PASSWORD, {
        context: { headers: { 'X-Language': i18n.language } },
    });

    const recoverPassword = async ({
        variables,
        onCompleted,
        onUpdate,
        onError,
    }: {
        variables?: RecoverPasswordMutationVariables;
        onCompleted?: (data?: RecoverPasswordMutation) => Promise<void>;
        onUpdate?: (status?: number | null, errors?: ({ field: string; messages: string[] } | null)[] | null) => void;
        onError?: (error: unknown) => Promise<void>;
    }) => {
        try {
            const { data } = await recoverPasswordMutation({
                variables,
                update: (cache, result) => {
                    const { status, errors } = result?.data?.recoverPassword || {};
                    if (onUpdate) onUpdate(status, errors);
                },
                onCompleted: (dat) => {
                    if (onCompleted) void onCompleted(dat);
                },
                onError: (err) => {
                    if (onError) void onError(err);
                },
            });

            return data?.recoverPassword;
        } catch (err) {
            if (onError) void onError(err);
            throw err;
        }
    };

    return { recoverPassword, loading, error };
};

export const getCurrentUser = async (accessToken: string, apolloClient: ApolloClient<NormalizedCacheObject>) => {
    const { data, errors } = await apolloClient.query<MeQuery>({
        query: ME,
        context: {
            headers: {
                'X-Auth-Token': accessToken,
            },
        },
        fetchPolicy: 'no-cache',
    });

    return {
        response: data?.me?.object,
        errors: data?.me?.errors,
        status: data?.me?.status,
        graphQlErrors: errors,
    };
};
